import { useState } from 'react';
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  initialen,
  mag,
  RECHTENSOORTEN,
  RECHTEN_LABEL,
  SENIORITEITEN,
  sorteerOpRechten,
  type OpdrachtRij,
  type PersoonRij,
} from '@vip/shared';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '../auth/useAuth';
import { doetMee } from '../begeleiding/begeleiding';
import { RechtenBadge } from './RechtenBadge';
import {
  ALLE,
  filterPersonen,
  GEEN_FILTERS,
  isGefilterd,
  type NiveauFilter,
  type RechtenFilter,
} from './filter';
import { usePersonen } from './personen.api';

/**
 * Wie er zijn. Eén regel per mens, en een klik brengt je naar zijn pagina.
 *
 * Vier kolommen, en dat kan omdat de naamkolom er twee regels van draagt: de
 * avatar zet de hoogte van de regel, dus het adres eronder kost geen ruimte die
 * er anders niet was. Zonder die tweede regel paste er drie kolommen in en zag
 * je van niemand waar hij zat.
 *
 * De kolommen vallen één voor één weg als het scherm smaller wordt: onder `lg`
 * de opdracht, onder `md` het niveau, onder `sm` de rechten. Het niveau komt
 * terug als regel onder de naam; de opdracht niet, want die naam is te lang voor
 * die breedte. Op een telefoon blijft de naam met het adres over, en de rest
 * lees je op zijn eigen pagina.
 *
 * Alles wat een kolom breder kan maken is hier een keuze en geen toeval: een
 * tabelcel breekt standaard niet af, dus lange tekst rekt de kolom op tot de
 * tabel horizontaal gaat schuiven. De naamkolom staat daarom op afbreken.
 *
 * `table-fixed` met vaste verhoudingen erbij, want anders verdeelt de browser
 * de breedte naar de langste inhoud. Dat is het e-mailadres, en dan slokt de
 * naamkolom op een breed scherm alles op terwijl de andere drie een restje
 * overhouden. Een verborgen kolom telt niet mee in die verdeling, dus op een
 * smaller scherm verdelen de overgebleven kolommen dezelfde verhouding
 * onderling.
 */
export function PersonenTab() {
  const { data, isLoading } = usePersonen();
  const { user } = useAuth();
  const [filters, setFilters] = useState(GEEN_FILTERS);
  const magBeheren = mag(user?.rechten, 'persoon:manage');

  // De server stuurt iedereen mee, want het begeleidingsbord leest dezelfde
  // lijst. Hier staat alleen wie je mag wijzigen: voor een Admin is dat de
  // hele club, voor een buddy zichzelf en zijn gecoachten.
  const personen = (data?.personen ?? []).filter((p) => doetMee(p) && p.magWijzigen);
  // Admins bovenaan, dan het Core Team, dan de buddy's, dan wie View Only heeft,
  // dan wie niet inlogt;
  // binnen een rang op naam. De regel staat in `shared`, met tests.
  const zichtbaar = sorteerOpRechten(filterPersonen(personen, filters));
  const gefilterd = isGefilterd(filters);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle className="flex items-baseline gap-2">
          Personen
          <span className="text-sm font-normal text-muted-foreground">
            {gefilterd ? `${zichtbaar.length} van ${personen.length}` : personen.length}
          </span>
        </CardTitle>
        {magBeheren && (
          <Button asChild>
            <Link to="/personen/nieuw">
              <PlusIcon data-icon="inline-start" />
              {/* Op een telefoon is de plus genoeg; het woord kost daar de
                  breedte die het zoekveld nodig heeft. */}
              <span className="hidden sm:inline">Persoon toevoegen</span>
            </Link>
          </Button>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <Zeef filters={filters} onWijzig={setFilters} opdrachtgevers={data?.opdrachtgevers ?? []} />

        {isLoading ? null : zichtbaar.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>
                {gefilterd ? 'Niemand gevonden.' : 'Er staat nog niemand in de app.'}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[65%] md:w-[55%] lg:w-[35%]">Naam</TableHead>
                <TableHead className="hidden w-[35%] sm:table-cell md:w-[25%] lg:w-[20%]">
                  Rechten
                </TableHead>
                <TableHead className="hidden w-[20%] md:table-cell lg:w-[15%]">Niveau</TableHead>
                <TableHead className="hidden w-[30%] lg:table-cell">Opdrachten</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zichtbaar.map((p) => (
                <Regel key={p.id} persoon={p} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Het zoekveld en de drie keuzelijsten, in de volgorde van de kolommen.
 *
 * Ze staan in één rij die afbreekt: op een breed scherm naast elkaar, op een
 * telefoon onder elkaar. Een apart filterscherm achter een knop was de andere
 * mogelijkheid, maar dat is een venster openen om drie dingen te kiezen.
 *
 * Op een telefoon passen er twee naast elkaar. Rechten en niveau zijn de korte
 * twee en delen die regel; de opdracht heeft de hele breedte nodig, want daar
 * staan namen van opdrachten in.
 */
function Zeef({
  filters,
  onWijzig,
  opdrachtgevers,
}: {
  filters: typeof GEEN_FILTERS;
  onWijzig: (filters: typeof GEEN_FILTERS) => void;
  opdrachtgevers: Array<{
    id: string;
    naam: string;
    opdrachten: Array<{ id: string; naam: string }>;
  }>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="w-full sm:max-w-56">
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          aria-label="Zoek een persoon"
          onChange={(e) => onWijzig({ ...filters, zoek: e.target.value })}
          placeholder="Zoeken"
          value={filters.zoek}
        />
        {filters.zoek && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              aria-label="Zoekterm wissen"
              onClick={() => onWijzig({ ...filters, zoek: '' })}
            >
              <XIcon />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>

      <Select
        onValueChange={(w) => onWijzig({ ...filters, rechten: w as RechtenFilter })}
        value={filters.rechten}
      >
        <SelectTrigger aria-label="Filter op rechten" className="w-[calc(50%-0.25rem)] sm:w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALLE}>Alle rechten</SelectItem>
          {/* Uit het model, in dezelfde volgorde als de vinkjes elders. */}
          {RECHTENSOORTEN.map((soort) => (
            <SelectItem key={soort} value={soort}>
              {RECHTEN_LABEL[soort]}
            </SelectItem>
          ))}
          <SelectItem value="geen">Geen</SelectItem>
        </SelectContent>
      </Select>

      <Select
        onValueChange={(w) => onWijzig({ ...filters, niveau: w as NiveauFilter })}
        value={filters.niveau}
      >
        <SelectTrigger aria-label="Filter op niveau" className="w-[calc(50%-0.25rem)] sm:w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALLE}>Alle niveaus</SelectItem>
          {SENIORITEITEN.map((n) => (
            <SelectItem key={n} value={n}>
              {n}
            </SelectItem>
          ))}
          {/* Dezelfde woorden als op de persoonspagina, waar dit veld leeg mag
              blijven. */}
          <SelectItem value="geen">Niet ingevuld</SelectItem>
        </SelectContent>
      </Select>

      <Select onValueChange={(w) => onWijzig({ ...filters, opdracht: w })} value={filters.opdracht}>
        <SelectTrigger aria-label="Filter op opdracht" className="w-full sm:w-52">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALLE}>Alle opdrachten</SelectItem>
          {/* Gegroepeerd op opdrachtgever: "Onderhoud Noord" zegt pas iets met
              de klant erboven, en die staat dan één keer in plaats van bij elke
              regel opnieuw. */}
          {opdrachtgevers.map((g) => (
            <SelectGroup key={g.id}>
              <SelectLabel>{g.naam}</SelectLabel>
              {g.opdrachten.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.naam}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Eén mens. De hele regel is de link, maar er staat er maar één in.
 *
 * `after:absolute after:inset-0` rekt het anker over de regel op, zodat je
 * overal kunt klikken zonder dat een schermlezer vijf keer dezelfde bestemming
 * voorleest. De regel zelf is daarvoor `relative`.
 */
function Regel({ persoon }: { persoon: PersoonRij }) {
  return (
    <TableRow className="relative cursor-pointer">
      {/* `whitespace-normal`: een tabelcel staat standaard op niet-afbreken, en
          dan rekt een lange naam of een lang adres de kolom op tot de tabel
          horizontaal gaat schuiven. Afbreken houdt alles binnen het scherm. */}
      <TableCell className="whitespace-normal">
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            <AvatarFallback className="text-xs">{initialen(persoon.naam)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Link
                className="font-medium after:absolute after:inset-0 focus-visible:underline"
                to={`/personen/${persoon.id}`}
              >
                {persoon.naam}
              </Link>
              {persoon.isZelf && <Badge variant="secondary">jij</Badge>}
            </div>
            {/* Het niveau valt hiernaast weg zodra het scherm smal wordt en
                komt hier terug. De opdracht niet: die naam is te lang voor deze
                breedte, en op een telefoon duwde hij de tabel uit beeld. */}
            {persoon.niveau && (
              <div className="text-muted-foreground md:hidden">{persoon.niveau}</div>
            )}
          </div>
        </div>
      </TableCell>

      {/* Op een telefoon eruit. Wat iemand mag lees je op zijn eigen pagina, en
          hier kost het de breedte die de naam nodig heeft. */}
      <TableCell className="hidden sm:table-cell">
        <div className="flex flex-wrap items-center gap-1">
          <RechtenBadge rechten={persoon.rechten} />
        </div>
      </TableCell>

      <TableCell className="hidden md:table-cell">{persoon.niveau ?? <Streep />}</TableCell>

      <TableCell className="hidden lg:table-cell">
        <Opdrachten opdrachten={persoon.opdrachten} />
      </TableCell>
    </TableRow>
  );
}

const Streep = () => <span className="text-muted-foreground">—</span>;

/** De eerste opdracht als tag, de rest als teller. Twee namen naast elkaar
 *  maken de kolom al te vol, en welke tweede het is lees je op zijn eigen
 *  pagina. De tag loopt af op de breedte van zijn kolom en niet op een vast
 *  aantal pixels, zodat een breed scherm ook echt meer naam laat zien. */
function Opdrachten({ opdrachten }: { opdrachten: OpdrachtRij[] }) {
  const [eerste, ...rest] = opdrachten;
  if (!eerste) return <Streep />;
  return (
    <div className="flex items-center gap-1">
      {/* `truncate` op een eigen span en niet op de badge: die is `inline-flex`,
          en dan hakt hij de tekst wel af maar zonder de drie puntjes. */}
      <Badge className="max-w-full" variant="outline">
        <span className="truncate">{eerste.naam}</span>
      </Badge>
      {rest.length > 0 && <span className="text-muted-foreground">+{rest.length}</span>}
    </div>
  );
}
